'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { IconLayoutGridAdd, IconRefresh } from '@tabler/icons-react';
import MediaGallerySurface from '@/components/media/MediaGallerySurface';
import HomeVideo from '@/components/media/HomeVideo';
import XSourceLink from './XSourceLink';
import AuthorIdentity from './AuthorIdentity';

const CARD_ACTIONS = [
  { id: 'recreate', Icon: IconRefresh },
  { id: 'remix', Icon: IconLayoutGridAdd },
];

function CardActionButton({ action, onAction }) {
  const t = useTranslations('GateD');
  const [showTooltip, setShowTooltip] = useState(false);
  const { id, Icon } = action;
  const label = t(`postDetail.${id}`);
  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
    >
      <button
        type="button"
        aria-label={label}
        onClick={(event) => {
          event.stopPropagation();
          onAction(id);
        }}
        onKeyDown={(event) => event.stopPropagation()}
        className="grid h-7 w-7 place-items-center rounded-lg border border-transparent bg-transparent p-0 text-white/78 transition duration-200 hover:scale-[1.05] hover:border-transparent hover:bg-white/[0.14] hover:text-white hover:backdrop-blur-sm focus:outline-none focus-visible:border-transparent focus-visible:bg-white/[0.14] focus-visible:text-white active:scale-[0.96]"
      >
        <Icon size={15} stroke={1.9} />
      </button>
      <div
        data-card-action-tooltip={id}
        className="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md border border-white/12 bg-[#0e1014]/95 px-2 py-1 text-[10px] font-medium text-white shadow-[0_8px_24px_rgba(0,0,0,.42)] backdrop-blur-sm transition-[opacity,margin] duration-150"
        style={{ opacity: showTooltip ? 1 : 0, marginBottom: showTooltip ? '8px' : '4px' }}
      >
        {label}
      </div>
    </div>
  );
}

export default function MediaCard({
  item,
  priority = false,
  onOpen = null,
  onAction = null,
  aspectRatio: forcedAspectRatio = null,
  homePreview = false,
}) {
  const t = useTranslations('GateD');
  const [hoverIntent, setHoverIntent] = useState(false);
  const media = item?.media?.[0];
  if (!item || !media?.url) return null;
  const isVideo = media.mediaType === 'video';
  const title = item.title || item.caption || t('common.untitledCreation');
  const hasDisplayName = Boolean(item.author?.displayName?.trim());
  const width = Number(media.width || 0);
  const height = Number(media.height || 0);
  const aspectRatio = forcedAspectRatio
    || (width > 0 && height > 0 ? `${width} / ${height}` : '4 / 5');
  const cardClassName = 'group relative block w-full min-h-36 cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] text-left shadow-[0_18px_50px_rgba(0,0,0,.16)] transition-all duration-300 hover:border-white/25 hover:shadow-[0_24px_65px_rgba(0,0,0,.32)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B51B] sm:min-h-48';
  const cardBody = (
    <>
      {homePreview && isVideo ? (
        <HomeVideo
          key={media.url}
          src={media.url}
          poster={media.thumbnailUrl}
          hoverIntent={hoverIntent}
          aria-label={media.altText || title}
          className="h-full min-h-36 w-full object-cover transition-transform duration-500 group-hover:scale-[1.025] motion-reduce:transition-none sm:min-h-48"
        />
      ) : <MediaGallerySurface
        src={isVideo ? media.url : (media.thumbnailUrl || media.url)}
        poster={media.thumbnailUrl || null}
        alt={media.altText || title}
        isVideo={isVideo}
        priority={priority}
        className="h-full min-h-36 w-full object-cover transition-transform duration-500 group-hover:scale-[1.025] motion-reduce:transition-none sm:min-h-48"
      />}
      {hasDisplayName || item.source?.canonicalUrl || typeof onAction === 'function' ? (
        <div className="absolute inset-x-0 bottom-0 translate-y-2 bg-gradient-to-t from-black/90 via-black/45 to-transparent p-3 pt-14 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100 [@media(hover:none)]:translate-y-0 [@media(hover:none)]:opacity-100">
          <div className="flex items-end justify-between gap-3">
            {hasDisplayName ? (
              <AuthorIdentity
                author={item.author}
                className="min-w-0 max-w-[calc(100%-8rem)] text-xs text-white/80"
                showDisplayName
                showUsername={false}
              />
            ) : <span />}
            <div className="flex shrink-0 items-center gap-1">
            {typeof onAction === 'function' ? (
              <div className="flex shrink-0 items-center gap-1.5">
                {CARD_ACTIONS.map((action) => (
                  <CardActionButton
                    key={action.id}
                    action={action}
                    onAction={(actionId) => onAction(item, actionId)}
                  />
                ))}
              </div>
            ) : null}
            <XSourceLink url={item.source?.canonicalUrl} />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );

  if (typeof onOpen === 'function') {
    return (
      <article
        className={cardClassName}
        style={{ aspectRatio }}
        onMouseEnter={() => setHoverIntent(true)}
        onMouseLeave={() => setHoverIntent(false)}
        onFocus={() => setHoverIntent(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setHoverIntent(false);
        }}
        onClick={() => onOpen(item)}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(item);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`${t('common.openCreation')}: ${title}`}
      >
        {cardBody}
      </article>
    );
  }

  return null;
}
